import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { X } from "lucide-react"

interface EmptyStateAction {
  label: string
  onClick: () => void
  icon?: React.ReactNode
  variant?: React.ComponentProps<typeof Button>["variant"]
  disabled?: boolean
  /** Tooltip text (rendered on a wrapping span so it fires while disabled). */
  title?: string
}

interface EmptyStateProps {
  illustration?: React.ReactNode
  title: string
  description?: React.ReactNode
  primaryAction?: EmptyStateAction
  secondaryAction?: EmptyStateAction
  /** Actions that are not plain buttons, e.g. a link; rendered after the button actions. */
  actions?: React.ReactNode
  bordered?: boolean
  hoverable?: boolean
  className?: string
  role?: "status" | "alert"
  dataTestId?: string
  /** When provided, renders a close button in the top-right corner. */
  onClose?: () => void
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  illustration,
  title,
  description,
  primaryAction,
  secondaryAction,
  actions,
  bordered = true,
  hoverable = false,
  className,
  role,
  dataTestId,
  onClose,
}) => {
  const { translate } = useTranslation()

  return (
    <Card
      role={role}
      data-testid={dataTestId}
      className={cn(
        "relative flex h-full flex-col items-center justify-center overflow-hidden rounded-2xl bg-background px-16 pt-14 pb-12 shadow-lg",
        bordered ? "border border-dashed border-grey4" : "border-none",
        hoverable &&
          "transition duration-300 ease-in-out hover:border-primary/55",
        className,
      )}
    >
      {onClose && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label={translate("CLOSE")}
          className="absolute top-3 right-3 z-10 h-8 w-8 text-grey5 hover:text-white"
        >
          <X />
        </Button>
      )}

      {/* Yellow halo glow */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 h-[360px] w-[640px] -translate-x-1/2 -translate-y-1/2 blur-sm"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(255,244,64,0.18) 0%, rgba(255,244,64,0.04) 40%, transparent 70%)",
        }}
      />

      <div className="relative flex flex-col items-center">
        {illustration && <div className="mb-7">{illustration}</div>}

        <h2 className="mb-2 text-center text-xl font-semibold tracking-tight text-white">
          {title}
        </h2>

        {description && (
          <p className="mx-auto mb-4 max-w-[520px] text-center text-sm text-muted-foreground">
            {description}
          </p>
        )}

        {(primaryAction || secondaryAction || actions) && (
          <div className="flex items-center justify-center gap-3">
            {primaryAction && (
              // The wrapping span (not disabled) carries the title: a native
              // `disabled` button gets `pointer-events:none` from Button, so
              // a title on the button itself would never show on hover.
              <span title={primaryAction.title}>
                <Button
                  variant={primaryAction.variant ?? "default"}
                  className="gap-2"
                  onClick={primaryAction.onClick}
                  disabled={primaryAction.disabled}
                >
                  {primaryAction.icon}
                  {primaryAction.label}
                </Button>
              </span>
            )}
            {secondaryAction && (
              <span title={secondaryAction.title}>
                <Button
                  variant={secondaryAction.variant ?? "outline"}
                  className="gap-2"
                  onClick={secondaryAction.onClick}
                  disabled={secondaryAction.disabled}
                >
                  {secondaryAction.icon}
                  {secondaryAction.label}
                </Button>
              </span>
            )}
            {actions}
          </div>
        )}
      </div>
    </Card>
  )
}
